<script>
export default {
    name: 'BaseCalendarViewDay',
};
</script>

<script setup>
import { useNow } from '@vueuse/core';
import { computed, ref, watchEffect } from 'vue';
import { addUnit } from '@/components/base-components/utils/dom';
import { getDailyCalendarGrid, getWeekday } from '@/components/base-components/components/calendar/utils/ranges.js';

const props = defineProps({
    date:           {
        type:     Date,
        required: true,
    },
    titleClass:     {
        type: [String, Array, Object],
    },
    gridClass:      {
        type: [String, Array, Object],
    },
});

const emit = defineEmits({
    range: null,
});

const day = computed(() => getWeekday(props.date));
const blank = computed(() => {
    const { date: _date, ...cell } = day.value;

    return cell;
});

const cells = computed(() => getDailyCalendarGrid(props.date));

watchEffect(() => {
    emit('range', [new Date(day.value.date), new Date(day.value.date)]);
});

const gridRef = ref(null);
const now = useNow({ interval: 10000 });

const pointerCellStyle = computed(() => {
    if (!day.value.isToday) return null;

    return {
        gridColumnStart: 2,
        gridRowStart:    now.value.getHours() + 2,
    };
});

const pointerStyle = computed(() => ({
    top:       addUnit(Math.round(now.value.getMinutes() * 100 / 60), '%'),
    translate: '0 -50%',
}));
</script>

<template>
    <div
        ref="gridRef"
        class="grid"
        :class="[
            gridClass || 'grid-cols-[auto_1fr] grid-rows-[auto_repeat(24,1fr)]',
        ]"
    >
        <slot
            v-bind="blank"
            name="title"
            :style="{
                gridColumnStart: 1,
                gridRowStart: 1,
            }"
        />

        <slot
            v-bind="day"
            name="title"
            :index="0"
            :style="{
                gridColumnStart: 2,
                gridRowStart: 1,
            }"
        />

        <template v-for="({ date, ...cell }, index) in cells">
            <slot
                v-bind="cell"
                name="cell"
                :styles="{
                    gridColumnStart: 1,
                    gridRowStart: cell.yIndex + 2,
                }"
            />

            <slot
                v-bind="{ date, ...cell }"
                name="cell"
                :index="index"
                :styles="{
                    gridColumnStart: 2,
                    gridRowStart: cell.yIndex + 2,
                }"
            />
        </template>

        <slot name="content" :days="[day]" />

        <slot
            v-if="pointerCellStyle"
            name="pointer-cell"
            :cell-style="pointerCellStyle"
            :pointer-style="pointerStyle"
        />
    </div>
</template>
